import {
  Badge,
  Column,
  EmptyState,
  FlexBox,
  IconButton,
  Page,
  RowId,
  SortStatus,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import type {
  SearchConversationEnriched,
  SearchParameters,
  SearchSort,
} from 'lib-search-types';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { Writable } from 'ts-essentials';

import { searchApi } from 'apis/search';
import { ConversationsTable } from 'components/conversation';
import { ConversationPreview } from 'components/conversation/conversationPreview';
import {
  type ConversationsTableSortableField,
  conversationsTableSortableFields,
} from 'components/conversation/conversationTable';
import { ConversationsBulkToolbar } from 'components/conversation/conversationsBulkToolbar/conversationsBulkToolbar';
import { useSelectConversations } from 'utils/conversation/useSelectConversations/useSelectConversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useServerStorage } from 'utils/useServerStorage';
import { useSplitter } from 'utils/useSplitter';

import { SearchFeedback } from './components/feedback/searchFeedback';
import { SearchFilters } from './components/filters/searchFilters';
import { SearchFiltersProvider } from './components/filters/searchFilters.context';
import { emptyFilters } from './components/filters/utils/definitions';
import { SearchInput } from './components/input/searchInput';
import { SavedSearchButton } from './components/savedSearch/button/savedSearchButton';
import { SavedSearchFormModal } from './components/savedSearch/form/savedSearchFormModal';
import { SavedSearchListModal } from './components/savedSearch/list/savedSearchListModal';
import { searchActions } from './search.actions';
import styles from './search.view.module.scss';
import { maxResultsCount } from './utils/results/maxResultsCount';
import { convertSortObjectToString } from './utils/sort/convertSort';
import { useSearchConversationEventsEffect } from './utils/useSearchConversationEventsEffect/useSearchConversationEventsEffect';
import { useSearchInit } from './utils/useSearchInit/useSearchInit';

const defaultSplitViewSize = 50;

export const SearchView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const flags = useFeatureFlag(['has-bulk-operations']);
  const { isInitialized } = useSearchInit();
  const { params } = useAppSelector((state) => state.search);

  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const storage = useServerStorage('user');
  const viewMode = isSmallScreen
    ? 'full'
    : (storage.data.conversations?.viewMode ?? 'full');
  const splitViewSize =
    storage.data.conversations?.splitViewSize ?? defaultSplitViewSize;

  const [previewedCsid, setPreviewedCsid] = useState<number | null>(null);

  const { containerRefFn, splitterRefFn, containerStyles, isResizing } =
    useSplitter({
      orientation: 'vertical',
      splitPosition: splitViewSize,
      startPanelMinSize: 200,
      endPanelMinSize: 200,
      onResizeEnd: (splitPosition) => {
        storage.set({
          conversations: (draft) => {
            draft.splitViewSize = splitPosition;
          },
        });
      },
      onDoubleClick: () => {
        storage.set({
          conversations: (draft) => {
            draft.splitViewSize = defaultSplitViewSize;
          },
        });
      },
    });

  /**
   * Whether the user has interacted with the table sorting. Once they have, their choice is
   * respected as-is, including clearing the sorting entirely.
   */
  const [hasUserSorted, setHasUserSorted] = useState(false);

  /**
   * The effective sortBy to use for querying the search results.
   */
  const effectiveSortBy = useMemo(():
    | SearchSort<'conversations'>
    | undefined => {
    // If `params.sortBy` is set, either from the URL or user interaction, it takes priority.
    if (params.sortBy) return params.sortBy;
    // If the user has searched or sorted, we don't want to override their choice with a default.
    if (params.query || hasUserSorted) return undefined;
    // Otherwise, default to `created_at:desc`.
    return { field: 'created_at', order: 'desc' };
  }, [params.sortBy, params.query, hasUserSorted]);

  useSearchConversationEventsEffect();

  const [
    readSearchResultsQuery,
    { asyncStatus, conversations, currentPage, totalPages, totalResults },
  ] = searchApi.useLazyReadAdvancedSearchResultsQuery({
    selectFromResult: ({
      data,
      isUninitialized,
      isFetching,
      isSuccess,
      isError,
    }) => ({
      asyncStatus: getAsyncStatusFromRtkQueryStates({
        isError,
        isFetching,
        isSuccess,
        isUninitialized,
      }),
      conversations: data?.data.results.map(({ result }) => result) ?? [],
      currentPage: data?.meta?.page ?? 1,
      totalPages: data?.meta?.total_pages ?? 1,
      totalResults: data?.meta?.total_results ?? 0,
    }),
  });

  const readSearchResultsQueryResultRef =
    useRef<ReturnType<typeof readSearchResultsQuery>>();

  const readSearchResults = useCallback(() => {
    if (readSearchResultsQueryResultRef.current) {
      readSearchResultsQueryResultRef.current.abort();

      // RTK skips new requests while the shared cache entry is still pending (abort settles async).
      dispatch(
        searchApi.internalActions.removeQueryResult({
          queryCacheKey: readSearchResultsQueryResultRef.current
            .queryCacheKey as Parameters<
            typeof searchApi.internalActions.removeQueryResult
          >[0]['queryCacheKey'],
        }),
      );
    }

    readSearchResultsQueryResultRef.current = readSearchResultsQuery({
      ...params,
      sortBy: effectiveSortBy,
      scope: 'conversations',
      enrichmentSpec: 'ALL',
    });
  }, [dispatch, params, effectiveSortBy, readSearchResultsQuery]);

  const {
    asyncStatus: selectConversationsAsyncStatus,
    eligibleBulkConversationIds,
    isCurrentPageAllConversationsSelected,
    isCurrentPageSomeConversationsSelected,
    selectedConversationIds,
    totalFilteredConversations,
    selectAllCurrentPageConversation,
    selectAllEligibleConversations,
    clearSelectedConversations,
    toggleSelectConversation,
    resetSelection,
  } = useSelectConversations({
    currentPageConversationIds: conversations.map(({ id }) => id) ?? [],
    onFetchConversationIdsBulk: async () => {
      const { data, meta } = await dispatch(
        searchApi.endpoints.readSelectiveSearch.initiate(
          {
            scope: 'conversations',
            query: params.query,
            filters: params.filters,
            isStrict: params.isStrict,
            selectFields: ['id'],
            pageSize: 1000,
          },
          { forceRefetch: true },
        ),
      ).unwrap();

      const csids = data.results.id?.map(Number) ?? [];

      return {
        csids,
        total_conversations: meta?.total_results,
        validations_failed: [],
      };
    },
  });

  const isAllEligibleConversationsSelected =
    selectedConversationIds.length &&
    selectedConversationIds.length === eligibleBulkConversationIds.length;

  useEffect(() => {
    if (isInitialized) {
      readSearchResults();
    }
  }, [isInitialized, readSearchResults]);

  // Handles resetting the selection when the search params change. This excludes the page param, as
  // this will change during pagination and we want to preserve the selection when that occurs.
  useUpdateEffect(() => {
    resetSelection();
  }, [params.query, params.filters, params.sortBy, params.isStrict]);

  const updateSearchParams = useCallback(
    (newParams: Partial<SearchParameters<'conversations'>>) => {
      dispatch(searchActions.params.update({ ...newParams, page: 1 }));
    },
    [dispatch],
  );

  const handleViewModeToggle = useCallback(() => {
    const nextMode = viewMode === 'full' ? 'split' : 'full';

    if (nextMode === 'full') {
      setPreviewedCsid(null);
    }

    storage.set({
      conversations: (draft) => {
        draft.viewMode = nextMode;
      },
    });

    ampli.searchViewModeToggled({ viewMode: nextMode });
  }, [storage, viewMode]);

  const handleRowClick = useCallback(
    (conversation: SearchConversationEnriched) => {
      if (viewMode === 'split') {
        setPreviewedCsid(conversation.id);
        ampli.searchConversationPreviewOpened({ csid: `${conversation.id}` });
      }
    },
    [viewMode],
  );

  const handleGoToConversation = useCallback(() => {
    ampli.searchConversationPreviewGoToClicked({ csid: `${previewedCsid}` });
  }, [previewedCsid]);

  const handleSubmitInput = useCallback(
    (query: string, isStrict: boolean) => {
      const nextParams: Partial<Writable<SearchParameters<'conversations'>>> = {
        query,
        isStrict,
      };

      if (!query) {
        nextParams.isStrict = false;
      }

      updateSearchParams(nextParams);

      ampli.searchFieldSubmitted({
        location: 'searchPage',
        isEmpty: !query,
        query,
      });
    },
    [updateSearchParams],
  );

  const handleClearInput = useCallback(() => {
    updateSearchParams({ query: '', isStrict: false });

    if (!params.filters.filters.length) {
      dispatch(searchActions.savedSearch.close());
    }

    ampli.searchFieldCleared({ location: 'searchPage' });
  }, [dispatch, params.filters.filters.length, updateSearchParams]);

  const handleClearFilters = useCallback(() => {
    updateSearchParams({ filters: emptyFilters, page: 1 });

    if (!params.query) {
      dispatch(searchActions.savedSearch.close());
    }

    ampli.searchFiltersCleared({ count: params.filters.filters.length });
  }, [
    dispatch,
    params.filters.filters.length,
    params.query,
    updateSearchParams,
  ]);

  const handleClearSavedSearch = useCallback(() => {
    dispatch(searchActions.savedSearch.close());

    navigate({
      path: '/search/conversations',
      params: {},
    });

    ampli.savedSearchClosed();
  }, [dispatch, navigate]);

  const handleRowSelect = useCallback(
    (rowId: RowId) => {
      toggleSelectConversation(rowId as number);

      ampli.conversationsSelectClicked({
        csid: rowId as number,
        selected: selectedConversationIds.includes(rowId as number),
      });
    },
    [toggleSelectConversation, selectedConversationIds],
  );

  const handleAllRowsSelect = useCallback(() => {
    if (isAllEligibleConversationsSelected) {
      resetSelection();
    } else {
      selectAllCurrentPageConversation();
    }

    ampli.conversationsSelectAllClicked();
  }, [
    isAllEligibleConversationsSelected,
    resetSelection,
    selectAllCurrentPageConversation,
  ]);

  const handlePreviousPage = useCallback(() => {
    dispatch(searchActions.params.update({ page: currentPage - 1 }));
    ampli.conversationsPreviousPageClicked();
  }, [dispatch, currentPage]);

  const handleNextPage = useCallback(() => {
    dispatch(searchActions.params.update({ page: currentPage + 1 }));
    ampli.conversationsNextPageClicked();
  }, [dispatch, currentPage]);

  const handleSortChange = useCallback(
    (newSortStatus: SortStatus | null) => {
      setHasUserSorted(true);

      const nextSortBy: SearchSort<'conversations'> | undefined = newSortStatus
        ? {
            field: newSortStatus.sortBy as ConversationsTableSortableField,
            order: newSortStatus.direction,
          }
        : undefined;

      updateSearchParams({ sortBy: nextSortBy });

      ampli.searchSortingUpdated({
        tab: 'conversations',
        type: convertSortObjectToString(nextSortBy) || 'relevance',
      });
    },
    [updateSearchParams],
  );

  const sortStatus = useMemo((): SortStatus | null => {
    if (
      !effectiveSortBy ||
      !conversationsTableSortableFields.includes(
        effectiveSortBy.field as ConversationsTableSortableField,
      )
    ) {
      return null;
    }

    return { sortBy: effectiveSortBy.field, direction: effectiveSortBy.order };
  }, [effectiveSortBy]);

  const showTotalResults =
    typeof totalResults === 'number' &&
    (!!params.query || params.filters.filters.length > 0);

  const showBulkActionsControls = selectedConversationIds.length > 0;

  const controls = (
    <div className={styles.controls}>
      {flags.hasBulkOperations && (
        <ConversationsBulkToolbar
          className={classNames(styles.control, styles.bulkActions, {
            [styles.active]: showBulkActionsControls,
          })}
          location="search"
          asyncStatus={selectConversationsAsyncStatus}
          eligibleBulkConversationIds={eligibleBulkConversationIds}
          selectedConversationIds={selectedConversationIds}
          totalFilteredConversations={totalFilteredConversations}
          onSelectAll={selectAllEligibleConversations}
          onClearAll={clearSelectedConversations}
        />
      )}

      <FlexBox
        className={classNames(styles.control, styles.filters, {
          [styles.active]: !showBulkActionsControls,
        })}
        gap="100"
        alignItems="center"
      >
        <SearchFiltersProvider>
          <SearchFilters />
        </SearchFiltersProvider>

        <SavedSearchButton
          buttonProps={{
            variant: 'tertiary',
          }}
        />

        {!isSmallScreen && (
          <Tooltip content={viewMode === 'full' ? 'Split view' : 'Full view'}>
            <IconButton
              icon={
                viewMode === 'full' ? 'screen-split-2-vertical' : 'screen-full'
              }
              variant="tertiary"
              onClick={handleViewModeToggle}
              aria-label={viewMode === 'full' ? 'Split view' : 'Full view'}
            />
          </Tooltip>
        )}
      </FlexBox>
    </div>
  );

  const table = (
    <ConversationsTable
      className={classNames(styles.table, {
        [styles.tableSplit]: viewMode === 'split',
      })}
      conversations={conversations}
      asyncStatus={asyncStatus}
      framed={viewMode === 'full'}
      onRowClick={viewMode === 'split' ? handleRowClick : undefined}
      highlightedRowId={
        viewMode === 'split' ? (previewedCsid ?? undefined) : undefined
      }
      emptyStateProps={
        params.filters.filters.length
          ? {
              title: 'No conversations found',
              description:
                'Your active filters might be hiding some conversations. Try adjusting or clearing them to see more results.',
              buttonProps: {
                children: 'Clear filters',
                variant: 'secondary',
                onClick: handleClearFilters,
              },
            }
          : {
              title: 'No conversations found',
              description: params.query
                ? 'Try a different search term or check the spelling.'
                : 'There are no conversations to show yet.',
            }
      }
      selectableRowsProps={
        conversations.length && flags.hasBulkOperations
          ? {
              selectedRowIds: selectedConversationIds as number[],
              allRowsSelected:
                asyncStatus === 'fulfilled' &&
                isCurrentPageAllConversationsSelected,
              allRowsSelectedIndeterminate:
                asyncStatus === 'fulfilled' &&
                isCurrentPageSomeConversationsSelected,
              onRowSelect: handleRowSelect,
              onAllRowsSelect: handleAllRowsSelect,
            }
          : undefined
      }
      sortingProps={{
        sortStatus,
        onSortChange: handleSortChange,
        sortableColumns: [
          'last_message_created_at',
          'created_at',
        ] satisfies ConversationsTableSortableField[],
      }}
      paginationProps={{
        previousButtonClick: handlePreviousPage,
        nextButtonClick: handleNextPage,
        previousButtonDisabled: currentPage <= 1 || asyncStatus === 'pending',
        nextButtonDisabled:
          currentPage >= totalPages || asyncStatus === 'pending',
      }}
    />
  );

  return (
    <Page>
      <Column.Row className={styles.inputRow} noMinHeight>
        <SearchInput
          onSearch={handleSubmitInput}
          onClear={handleClearInput}
          onSavedSearchClear={handleClearSavedSearch}
        />
      </Column.Row>

      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            Conversations
            {showTotalResults && (
              <Badge variant="secondary" size="medium">
                {Math.min(totalResults, maxResultsCount)
                  .toLocaleString(navigator.language)
                  .concat(totalResults >= maxResultsCount ? '+' : '')}
              </Badge>
            )}
          </FlexBox>
        }
        controls={controls}
      />

      <Page.Body>
        <Page.Section>
          {viewMode === 'split' ? (
            <div
              ref={containerRefFn}
              className={classNames(styles.splitContainer, {
                [styles.isResizing]: isResizing,
              })}
              style={containerStyles}
            >
              <div className={styles.splitPane}>{table}</div>

              <div className={styles.splitPane}>
                <div ref={splitterRefFn} className={styles.splitResizer} />

                {previewedCsid ? (
                  <ConversationPreview
                    csid={previewedCsid}
                    onNavigateToConversation={handleGoToConversation}
                  />
                ) : (
                  <FlexBox
                    justifyContent="center"
                    alignItems="center"
                    padding="500"
                    height="100%"
                  >
                    <EmptyState
                      icon="eye"
                      title="Preview a conversation"
                      description={
                        <>
                          <Text.Paragraph withMarginBottom>
                            Select a conversation from the list to view messages
                            and other details.
                          </Text.Paragraph>

                          <SearchFeedback />
                        </>
                      }
                    />
                  </FlexBox>
                )}
              </div>
            </div>
          ) : (
            table
          )}
        </Page.Section>
      </Page.Body>

      <SavedSearchFormModal />
      <SavedSearchListModal />
    </Page>
  );
};
